import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "react-toastify"

import { HTTP_METHODS } from "@/constants/httpMethods"
import type { GeneralObject } from "@/types"
import { filterData } from "@/utils/helpers"

import useAxios from "./useAxios"
import useFormErr from "./useFormErr"

interface UseGenereicMutationParams<
  TParams extends GeneralObject = GeneralObject,
> {
  createEndpoint: (data: TParams) => string
  method: (typeof HTTP_METHODS)[keyof typeof HTTP_METHODS]
  invalidationKeys?: Array<Array<string>>
  enabled?: boolean
  silent?: boolean
}

export function useGenereicMutation<TParams extends GeneralObject>({
  createEndpoint,
  method,
  invalidationKeys,
  silent,
}: UseGenereicMutationParams<TParams>) {
  const axios = useAxios()
  const { handleMutationErr } = useFormErr()
  const queryClient = useQueryClient()

  const action = (() => {
    if (method === HTTP_METHODS.POST)
      return (data: TParams) =>
        axios.post(createEndpoint(data), data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
    else if (method === HTTP_METHODS.PUT)
      return (data: TParams) =>
        axios.post(
          createEndpoint(data),
          { ...data, _method: "PUT" },
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        )
    else if (method === HTTP_METHODS.DELETE)
      return (data: TParams) => axios.delete(createEndpoint(data))
    else if (method === HTTP_METHODS.GET)
      return (data: TParams) => axios.get(createEndpoint(data))
    else {
      const _exhaustiveCheck: never = method
      return _exhaustiveCheck
    }
  })()

  return useMutation({
    mutationFn: (data: TParams) => action(filterData(data)),
    onSuccess: (data) => {
      if (!silent) {
        toast.success(data.data.message)
      }
      invalidationKeys?.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key })
      )
    },
    onError: handleMutationErr,
  })
}
